/**
 * Modal: bulk-assign walkthrough for screenshot-only transactions.
 *
 * Extracted from AdminApprovals.jsx during the iteration-49 refactor.
 * Walks the merchant through a queue of screenshot-only txns one at a
 * time, auto-running OCR on each entry.
 */
import { useEffect, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";


/**
 * Bulk-assign walkthrough: takes a queue of screenshot-only txns and
 * walks the merchant through them one at a time. Auto-runs OCR on
 * each row entry, then offers Skip / Save / Save&Approve. After the
 * last row the modal closes automatically.
 *
 * Implementation notes:
 *  • The queue is a SNAPSHOT taken at the time the modal opens. New
 *    rows added to the page after the modal opens DON'T get appended
 *    mid-walkthrough — that would feel unpredictable. Merchant can
 *    re-open the bulk modal to pick them up.
 *  • Optimistic update via onRowSaved fires after EACH save so the
 *    background table reflects progress even before the walkthrough
 *    ends — merchant feels the queue shrink as they go.
 *  • Auto-OCR on row enter is deliberately fire-and-forget; if it
 *    soft-fails, the manual input is still usable instantly.
 */
export default function BulkAssignUtrModal({ queue, scope, onClose, onRowSaved, onApproveRow }) {
    const [idx, setIdx] = useState(0);
    const [utr, setUtr] = useState("");
    const [ocrInfo, setOcrInfo] = useState(null);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [saveBusy, setSaveBusy] = useState(false);
    const [approveBusy, setApproveBusy] = useState(false);
    const [err, setErr] = useState("");
    const [doneCount, setDoneCount] = useState(0);
    const base = scope === "admin" ? "/admin" : "/partner";
    const current = queue[idx];
    const total = queue.length;

    // Auto-run OCR every time the merchant moves to a new row.
    useEffect(() => {
        let cancelled = false;
        setUtr(""); setOcrInfo(null); setErr("");
        if (!current?.screenshot) return;
        setOcrBusy(true);
        (async () => {
            try {
                // Hybrid: client-side Tesseract pass first.
                if (current.screenshot && current.screenshot.startsWith("data:")) {
                    try {
                        const { extractUtrClientSide } = await import("@/lib/clientOcr");
                        const resp = await fetch(current.screenshot);
                        const blob = await resp.blob();
                        const clientRes = await extractUtrClientSide(blob);
                        if (clientRes.utr) {
                            if (cancelled) return;
                            setOcrInfo({ ok: true, utr: clientRes.utr, payment_app: "Other", provenance: "client" });
                            setUtr(clientRes.utr);
                            setOcrBusy(false);
                            return;
                        }
                    } catch (clientErr) { console.warn("client-side OCR:", clientErr); }
                }
                const r = await api.post(`${base}/transactions/${current.txn_id}/rerun-ocr`);
                if (cancelled) return;
                setOcrInfo(r.data);
                if (r.data?.utr) setUtr(r.data.utr);
            } catch (e) {
                if (!cancelled) {
                    setOcrInfo({ ok: false, reason: formatDetail(e?.response?.data?.detail) || "OCR rerun failed" });
                }
            } finally {
                if (!cancelled) setOcrBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [current?.txn_id, base, current?.screenshot]);

    const advance = () => {
        if (idx + 1 >= total) onClose();
        else setIdx(idx + 1);
    };
    const saveOnly = async () => {
        if (utr.trim().length < 6) { setErr("UTR must be at least 6 characters"); return; }
        setSaveBusy(true); setErr("");
        try {
            await api.post(`${base}/transactions/${current.txn_id}/assign-utr`, { utr: utr.trim() });
            onRowSaved(current.txn_id, utr.trim());
            setDoneCount((n) => n + 1);
            advance();
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || "Could not save UTR");
        } finally { setSaveBusy(false); }
    };
    const saveAndApprove = async () => {
        if (utr.trim().length < 6) { setErr("UTR must be at least 6 characters"); return; }
        setApproveBusy(true); setErr("");
        try {
            await api.post(`${base}/transactions/${current.txn_id}/assign-utr`, { utr: utr.trim() });
            onRowSaved(current.txn_id, utr.trim());
            await onApproveRow(current.txn_id);
            setDoneCount((n) => n + 1);
            advance();
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || "Could not save & approve");
        } finally { setApproveBusy(false); }
    };
    const skip = () => { advance(); };
    // `busy` consolidates the three per-row in-flight flags so the
    // keyboard handler + JSX both reference one variable.
    const busy = ocrBusy || saveBusy || approveBusy;

    // ── Keyboard shortcuts (power-agent mode) ───────────────────────
    //   Enter        → Save & Approve  (the default power action)
    //   Shift+Enter  → Save & next     (no approve — useful when the
    //                  agent wants to do a final eyeball pass at the
    //                  end before bulk-approving)
    //   Esc          → Skip            (works even with input focused)
    // We attach at the document level + preventDefault so the Enter
    // keypress on the autoFocused input doesn't ALSO try to submit a
    // surrounding form (Modal sometimes wraps content in <form>).
    // Guard against firing while busy so a fast Enter+Enter doesn't
    // double-submit.
    useEffect(() => {
        const onKey = (ev) => {
            if (ev.key === "Escape") {
                ev.preventDefault();
                if (!busy) skip();
                return;
            }
            if (ev.key === "Enter") {
                if (busy) { ev.preventDefault(); return; }
                if (utr.trim().length < 6) return;  // let the default beep
                ev.preventDefault();
                if (ev.shiftKey) saveOnly();
                else saveAndApprove();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // saveOnly/saveAndApprove/skip are inline closures over current
        // state; we want the listener to see the freshest copy each
        // render, so re-bind whenever any dep changes.
    }, [utr, busy, idx]);

    if (!current) return null;
    return (
        <Modal testId="bulk-assign-utr-modal" onClose={busy ? null : onClose}
               title={`Bulk Assign UTRs · ${idx + 1} of ${total}`} size="md">
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div data-testid="bulk-assign-progress"
                             className="h-full bg-emerald-500 transition-all duration-300"
                             style={{ width: `${total ? (idx / total) * 100 : 0}%` }} />
                    </div>
                    <div className="text-[10px] font-medium text-slate-500 tabular-nums">
                        {doneCount} done
                    </div>
                </div>

                <div className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
                    <div>
                        <span className="font-mono text-slate-700">{current.customer_username}</span>
                        <span className="mx-1.5 text-slate-300">·</span>
                        <span className="font-mono text-slate-900 font-semibold">{fmtINR(current.amount)}</span>
                    </div>
                    <span className="font-mono text-[10px] text-slate-500">{current.txn_id}</span>
                </div>

                {current.screenshot ? (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2">
                        <img src={current.screenshot} alt="screenshot"
                             className="w-full max-h-[35vh] object-contain rounded" />
                    </div>
                ) : (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
                        No screenshot stored. Type the UTR manually below.
                    </div>
                )}

                {ocrBusy && (
                    <div data-testid="bulk-assign-ocr-busy" className="text-[11px] rounded-md p-2 bg-slate-50 border border-slate-200 text-slate-600 flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Running smart OCR on screenshot…
                    </div>
                )}
                {!ocrBusy && ocrInfo?.ok && ocrInfo?.utr && (
                    <div data-testid="bulk-assign-ocr-success" className="text-[11px] rounded-md p-2 bg-emerald-50 border border-emerald-200 text-emerald-800">
                        <span className="font-medium">Detected:</span> UTR <span className="font-mono">{ocrInfo.utr}</span> — verify and Save.
                    </div>
                )}
                {!ocrBusy && ocrInfo && !ocrInfo.ok && (
                    <div data-testid="bulk-assign-ocr-softfail" className="text-[11px] rounded-md p-2 bg-amber-50 border border-amber-200 text-amber-900">
                        <span className="font-medium">OCR couldn&apos;t read this image.</span>{" "}
                        {ocrInfo.reason || "Type the UTR you see in the screenshot."}
                    </div>
                )}

                <div>
                    <label className="label">UTR / Reference number</label>
                    <input
                        data-testid="bulk-assign-utr-input"
                        value={utr}
                        onChange={(e) => setUtr(e.target.value)}
                        className="input font-mono"
                        placeholder="12-digit reference"
                        inputMode="numeric"
                        autoFocus
                    />
                </div>

                {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

                <div className="flex justify-between items-center gap-2 pt-1">
                    <button
                        data-testid="bulk-assign-skip"
                        type="button"
                        onClick={skip}
                        disabled={busy}
                        className="btn-secondary px-3 py-1.5 rounded-md text-xs disabled:opacity-40"
                    >Skip →</button>
                    <div className="flex gap-2">
                        <button
                            data-testid="bulk-assign-save"
                            type="button"
                            onClick={saveOnly}
                            disabled={busy || utr.trim().length < 6}
                            className="btn-secondary border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-40"
                        >
                            {saveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Save &amp; next
                        </button>
                        <button
                            data-testid="bulk-assign-save-approve"
                            type="button"
                            onClick={saveAndApprove}
                            disabled={busy || utr.trim().length < 6}
                            className="btn-emerald px-4 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-40"
                        >
                            {approveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                            Save &amp; Approve
                        </button>
                    </div>
                </div>

                {/* Power-agent keyboard hints — tiny footer so the
                    shortcuts are discoverable without cluttering the
                    main UI. <kbd> styling matches the rest of the app. */}
                <div data-testid="bulk-assign-kbd-hints" className="flex items-center justify-center gap-3 pt-1 text-[10px] text-slate-400 select-none">
                    <span><kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono">Enter</kbd> Save &amp; Approve</span>
                    <span><kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono">⇧ Enter</kbd> Save &amp; next</span>
                    <span><kbd className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-600 font-mono">Esc</kbd> Skip</span>
                </div>
            </div>
        </Modal>
    );
}
